import { useSelector } from 'react-redux';
import { RootState, dispatch } from '../../store';
import { AuthState } from '../../store/slices/auth.slice';
import { useEffect, useRef, useState } from 'react';
import cc from 'classcat';
import { useLocation } from 'react-router';
import ProfileDropdown from '../ProfileDropdown';
import { ProfileImage } from '../Common/ProfileImage';
import { selectNavigationData, selectSideBarCollapsed, updateMobileSideBarOpen } from '../../store/slices/navigation/navigation.slice';
import { useTranslation } from '../../hooks/useTranslation';
import { Translation } from '../../commonTypes/translation.types';
import { DateTime } from 'luxon';
import { formatToWeekdayMonthDayYear } from '../../util/date/dateFormater';
import Notifications from '../Notifications';
import { selectHasUnreadNotifications } from '../../store/slices/notification-center.slice';
import { useHttpGetNotificationsByUserOnMount, useHttpGetOrganizationByIdOnMount } from '../../hooks';
import { selectOrganization } from '../../store/slices/organization.slice';

export const Header = () => {
    const { t } = useTranslation();
    const auth = useSelector<RootState, AuthState>(state => state.auth);
    const headerData = useSelector(selectNavigationData);
    const location = useLocation();
    const collapsed = useSelector(selectSideBarCollapsed);
    const [userMenuVisible, setUserMenuVisible] = useState<boolean>(false);
    const currentDate = DateTime.now();
    const [notificationsVisible, setNotificationsVisible] = useState<boolean>(false);
    const notificationsRef = useRef<HTMLDivElement | null>(null);
    const notificationButtonRef = useRef<HTMLDivElement | null>(null);

    useHttpGetNotificationsByUserOnMount();
    useHttpGetOrganizationByIdOnMount(auth.value.organizationId);

    const hasUnreadNotifications = useSelector(selectHasUnreadNotifications);

    const notificationIcon = hasUnreadNotifications
        ? `/svg/notification-unread.svg`
        : notificationsVisible
        ? `/svg/notification-active.svg`
        : `/svg/notification.svg`;

    const fullName = (): string => {
        const { givenName, familyName } = auth.value.profile;
        if (givenName && familyName) return `${givenName} ${familyName}`;
        return auth.value.email ? auth.value.email : 'Name not provided';
    };

    const organization = useSelector(selectOrganization);

    const checkSelectedSubItem = () => {
        const pathname = location.pathname;

        if (pathname.match(/^\/clients\/[^/]+$/) && pathname !== '/clients') return '';

        for (let item of headerData) {
            if (item.keyTitle === 'invoicing' || item.keyTitle === 'performance') continue;

            if (item.subMenuItems.length > 0) {
                for (let sub of item.subMenuItems) {
                    if (sub.url === location.pathname) return t(`navigation.${sub.keyTitle}` as keyof Translation);
                }
            }
            if (item.hasParams && location.pathname.startsWith(item.url)) return t(`navigation.${item.keyTitle}` as keyof Translation);
            if (item.url === location.pathname) return t(`navigation.${item.keyTitle}` as keyof Translation);
        }

        if (pathname.startsWith('/profile')) return t('profileView.profile');
        if (pathname.startsWith('/create-policy')) return t('organizationCommon.organization');
        if (pathname.startsWith('/holidays')) return t('holidays.pageTitle');

        return '';
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                notificationsRef.current &&
                !notificationsRef.current.contains(event.target as Node) &&
                notificationButtonRef.current &&
                !notificationButtonRef.current.contains(event.target as Node)
            ) {
                setNotificationsVisible(false);
            }
        };

        const handleScroll = () => {
            setNotificationsVisible(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleNotificationClick = () => {
        setNotificationsVisible(prev => !prev);
    };

    const isDashboardPage = location.pathname === '/';
    const isBillingPage = location.pathname === '/subscription';

    return (
        <>
            <header className={cc(['header2', { 'header2--extended': collapsed }])}>
                <div className="header2__title-container"></div>
                <div className="header2__mobile-logo">
                    <img src="/svg/fornul.io-nav-logo.svg" alt="logo" width={118} height={24} />
                </div>
                <div className="header2__content">
                    <div className="header2__notification" ref={notificationButtonRef} onClick={handleNotificationClick}>
                        <img key={notificationIcon} className="header2__notification-image" src={notificationIcon} alt="Notification" />
                    </div>
                    {notificationsVisible && <Notifications ref={notificationsRef} />}
                    <div className={cc(['header2__user', userMenuVisible && 'header2__profile-dropdown'])}>
                        <button className="header2__user--button" onClick={() => setUserMenuVisible(prev => !prev)}>
                            <div className="header2__user-data">
                                <ProfileImage className="header2__user-image" url={auth.value.profile.imageUrl} alt="User" />
                                <div>
                                    <p className="header2__user-full-name">{fullName()}</p>
                                    <p className="header2__user-organization">{organization.name}</p>
                                </div>
                            </div>
                            <img className="header2__arrow" src="/svg/arrow-down.svg" alt="More" />
                        </button>
                        {userMenuVisible && (
                            <>
                                <div className="header2__user-menu">
                                    <ProfileDropdown setUserMenuVisible={setUserMenuVisible} />
                                </div>
                                <div className="header2__event-bubbling-mask" onClick={() => setUserMenuVisible(false)}></div>
                            </>
                        )}
                    </div>

                    <button className="header2__hamburger" onClick={() => dispatch(updateMobileSideBarOpen(true))}>
                        <img src="/svg/hamburger.svg" alt="menu" />
                    </button>
                </div>
            </header>
            {checkSelectedSubItem() && (
                <div className={cc(['header2__title-container-new', { 'header2__title-container-new--extended': collapsed }])}>
                    <h1 className="header2__title">{checkSelectedSubItem()}</h1>
                    {isDashboardPage && <h6 className="header2__date">{formatToWeekdayMonthDayYear(currentDate)}</h6>}
                    {isBillingPage && <p className="header2__subtitle">{t('billing.subtitle')}</p>}
                </div>
            )}
        </>
    );
};
